import { useState, useRef, useEffect, FormEvent } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl } from '../utils/api';
import './AIAgentPage.css';

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    suggestedActions?: { type: string; label: string }[];
}

function AIAgentPage() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (messages.length === 0) {
            setMessages([
                {
                    id: '1',
                    role: 'assistant',
                    content: "👋 Welcome! I'm your AI customer service assistant. I can help answer questions about products, shipping, returns, and more. How can I help you today?",
                    timestamp: new Date(),
                },
            ]);
        }
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (e: FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isTyping) return;

        const userMessage: Message = {
            id: `msg_${Date.now()}`,
            role: 'user',
            content: inputValue.trim(),
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        try {
            const response = await fetch(getApiUrl('/api/ai-agent/chat'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('artisan_token')}`,
                },
                body: JSON.stringify({
                    message: userMessage.content,
                    conversationHistory: messages.map(m => ({
                        role: m.role,
                        content: m.content,
                    })),
                    storeInfo: {
                        name: 'My Artisan Shop',
                        specialties: ['handmade ceramics', 'pottery'],
                    },
                }),
            });

            if (!response.ok) throw new Error('Chat failed');

            const data = await response.json();

            const assistantMessage: Message = {
                id: `msg_${Date.now()}`,
                role: 'assistant',
                content: data.response,
                timestamp: new Date(),
                suggestedActions: data.suggestedActions,
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            toast.error('Failed to send message');
            setMessages(prev => [
                ...prev,
                {
                    id: `msg_${Date.now()}`,
                    role: 'assistant',
                    content: "I'm sorry, I encountered an error. Please try again.",
                    timestamp: new Date(),
                },
            ]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleQuickAction = (action: { type: string; label: string }) => {
        toast.info(`Action: ${action.label}`);
    };

    const stats = {
        activeConversations: 3,
        resolvedToday: 12,
        avgResponseTime: '< 5 sec',
        satisfaction: '94%',
    };

    return (
        <div className="ai-agent-page animate-fade-in">
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <span className="title-icon">🤖</span>
                        AI Customer Service
                    </h1>
                    <p className="page-subtitle">
                        Your 24/7 AI assistant for customer inquiries
                    </p>
                </div>
                <button
                    className="btn btn-secondary"
                    onClick={() => setShowSettings(!showSettings)}
                >
                    ⚙️ Configure
                </button>
            </div>

            <div className="stats-row">
                <div className="stat-item">
                    <span className="stat-value">{stats.activeConversations}</span>
                    <span className="stat-label">Active</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.resolvedToday}</span>
                    <span className="stat-label">Resolved</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.avgResponseTime}</span>
                    <span className="stat-label">Response</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.satisfaction}</span>
                    <span className="stat-label">Satisfaction</span>
                </div>
            </div>

            <div className="chat-container">
                <div className="chat-header">
                    <span className="chat-status online"></span>
                    <span>Test Chat - Preview how customers will interact</span>
                </div>

                <div className="messages-container">
                    {messages.map(message => (
                        <div
                            key={message.id}
                            className={`message ${message.role}`}
                        >
                            <div className="message-content">
                                {message.content}
                            </div>
                            {message.suggestedActions && message.suggestedActions.length > 0 && (
                                <div className="suggested-actions">
                                    {message.suggestedActions.map((action, i) => (
                                        <button
                                            key={i}
                                            className="action-btn"
                                            onClick={() => handleQuickAction(action)}
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <span className="message-time">
                                {message.timestamp.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </span>
                        </div>
                    ))}

                    {isTyping && (
                        <div className="message assistant typing">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                <form className="chat-input-form" onSubmit={sendMessage}>
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Test a customer message..."
                        disabled={isTyping}
                    />
                    <button type="submit" className="send-btn" disabled={isTyping || !inputValue.trim()}>
                        ➤
                    </button>
                </form>
            </div>

            {showSettings && (
                <div className="settings-panel card animate-slide-up">
                    <h3>AI Agent Configuration</h3>

                    <div className="setting-group">
                        <label>Response Personality</label>
                        <select defaultValue="friendly">
                            <option value="friendly">Friendly & Warm</option>
                            <option value="professional">Professional</option>
                            <option value="casual">Casual</option>
                        </select>
                    </div>

                    <div className="setting-group">
                        <label>Escalation Trigger</label>
                        <select defaultValue="auto">
                            <option value="auto">Automatic (recommended)</option>
                            <option value="manual">Manual only</option>
                            <option value="never">Never escalate</option>
                        </select>
                    </div>

                    <div className="setting-group">
                        <label>Product Knowledge</label>
                        <div className="toggle-row">
                            <span>Include all products</span>
                            <label className="toggle-switch">
                                <input type="checkbox" defaultChecked />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>

                    <div className="setting-group">
                        <label>Supported Languages</label>
                        <div className="checkbox-group">
                            <label><input type="checkbox" defaultChecked /> English</label>
                            <label><input type="checkbox" defaultChecked /> Spanish</label>
                            <label><input type="checkbox" /> French</label>
                            <label><input type="checkbox" /> German</label>
                        </div>
                    </div>

                    <button className="btn btn-primary" onClick={() => setShowSettings(false)}>
                        Save Configuration
                    </button>
                </div>
            )}

            <div className="embed-info card">
                <h3>💬 Add to Your Store</h3>
                <p>Embed this chat widget on your Shopify store to provide 24/7 customer support.</p>
                <button className="btn btn-secondary">
                    Get Embed Code
                </button>
            </div>
        </div>
    );
}

export default AIAgentPage;
